import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Button,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
  Paper,
  Badge,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Work as WorkIcon,
  Bookmark as BookmarkIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Notifications as NotificationsIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as PendingIcon,
  BookmarkBorder as BookmarkBorderIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ApiService from '../../services/api';
import { useCurrency } from '../../context/CurrencyContext';
import DatabaseService from '../../services/DatabaseService';
import socketService from '../../services/SocketService';
import { calculateJobSeekerCompletion } from '../../utils/profileCompletion';

const Dashboard = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { formatCurrency } = useCurrency();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [dashboardStats, setDashboardStats] = useState({
    appliedJobs: 0,
    savedJobs: 0,
    profileViews: 0,
    profileCompletion: 0,
  });
  const [recentApplications, setRecentApplications] = useState([]);
  const [savedJobs, setSavedJobs] = useState([]);
  const [jobRecommendations, setJobRecommendations] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [quickSearchParams, setQuickSearchParams] = useState({
    keyword: '',
    location: '',
    type: 'all',
  });

  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        
        // Load dashboard data
        const uid = currentUser.uid || currentUser._id;
        const [userData, savedJobsResponse, recommendationsResponse] = await Promise.all([
          DatabaseService.getUserProfile(uid),
          ApiService.getSavedJobs(uid),
          ApiService.getJobs({ limit: 5 })
        ]);

        const profile = userData.profileData || userData.jobSeekerProfile || userData.profile || {};
        setProfileData(profile);
        const saved = savedJobsResponse.savedJobs || savedJobsResponse.data?.savedJobs || [];
        setSavedJobs(saved);

        const recommendations = recommendationsResponse.jobs || recommendationsResponse.data?.jobs || [];
        setJobRecommendations(recommendations);

        // Load applications
        const applications = await ApiService.getApplicationsByUser(currentUser._id || uid, { limit: 5 });
        const rawApps = (applications.applications || applications.data?.applications || []).slice(0, 5);
        const mappedApps = rawApps.map(app => ({
          id: app._id || app.id,
          jobId: app.job?._id || app.jobId,
          jobTitle: app.job?.title || app.jobTitle || 'N/A',
          companyName: app.job?.companyName || app.companyName || 'N/A',
          location: app.job?.location || app.location || null,
          appliedAt: new Date(app.createdAt || app.appliedDate || Date.now()).toLocaleDateString(),
          status: app.status || 'submitted'
        }));
        setRecentApplications(mappedApps);

        // Load notifications
        const userNotifications = await DatabaseService.getUserNotifications(uid, 5);
        setNotifications(userNotifications || []);

        // Calculate stats
        const completion = calculateJobSeekerCompletion(profile);
        setDashboardStats({
          appliedJobs: (applications.applications || applications.data?.applications || []).length || 0,
          savedJobs: saved.length,
          profileViews: userData.analytics?.profileViews || 0,
          profileCompletion: completion.percent,
        });
        
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      loadDashboardData();
      
      // Setup Socket.IO listeners for real-time updates
      const unsubscribeApplicationUpdate = socketService.subscribe('application_update', (data) => {
        if (data.applicantId === currentUser.uid) {
          // Update application status in real-time
          setRecentApplications(prev => 
            prev.map(app => 
              app.id === data.applicationId 
                ? { ...app, status: data.status, updatedAt: new Date() }
                : app
            )
          );
        }
      });
      
      const unsubscribeJobRecommendation = socketService.subscribe('job_recommendation', (data) => {
        if (data.userId === currentUser.uid) {
          // Add new recommended job
          setJobRecommendations(prev => [data.job, ...prev.slice(0, 2)]);
        }
      });
      
      const unsubscribeJobAlert = socketService.subscribe('job_alert', (data) => {
        if (data.userId === currentUser.uid) {
          // Add to notifications
          setNotifications(prev => [{
            id: Date.now(),
            title: 'Job Alert',
            message: `${data.job.title} matches your preferences`,
            read: false,
            createdAt: new Date().toISOString()
          }, ...prev.slice(0, 4)]);
        }
      });
      
      // Cleanup function
      return () => {
        unsubscribeApplicationUpdate();
        unsubscribeJobRecommendation();
        unsubscribeJobAlert();
      };
    }
  }, [currentUser]);

  // Completion calculation is handled by shared utility

  const handleQuickSearch = () => {
    const searchQuery = new URLSearchParams(quickSearchParams).toString();
    navigate(`/jobseeker/jobs?${searchQuery}`);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'interviewed':
      case 'shortlisted':
        return 'info';
      case 'hired':
      case 'accepted':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <PendingIcon />;
      case 'interviewed':
      case 'shortlisted':
        return <ScheduleIcon />;
      case 'hired':
      case 'accepted':
        return <CheckCircleIcon />;
      case 'rejected':
        return <CancelIcon />;
      default:
        return <WorkIcon />;
    }
  };

  const toggleSaveJob = async (jobId) => {
    try {
      const isSaved = savedJobs.some(job => job._id === jobId);
      if (isSaved) {
        await ApiService.unsaveJob(currentUser._id || currentUser.uid, jobId);
        setSavedJobs(savedJobs.filter(job => job._id !== jobId));
      } else {
        await ApiService.saveJob(currentUser._id || currentUser.uid, jobId);
        // Reload saved jobs
        const updated = await ApiService.getSavedJobs(currentUser._id || currentUser.uid);
        setSavedJobs(updated.savedJobs || []);
      }
    } catch (error) {
      console.error('Error toggling job save:', error);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Welcome back, {profileData?.personalInfo?.firstName || 'Job Seeker'}!
        </Typography>
        <Badge badgeContent={notifications.filter(n => !n.read).length} color="error">
          <IconButton onClick={() => navigate('/jobseeker/notifications')}>
            <NotificationsIcon />
          </IconButton>
        </Badge>
      </Box>

      {/* Profile Completion Alert */}
      {dashboardStats.profileCompletion < 100 && (
        <Alert severity="info" sx={{ mb: 3 }} action={
          <Button color="inherit" size="small" onClick={() => navigate('/jobseeker/profile', { state: { openEdit: true } })}>
            Complete your profile
          </Button>
        }>
          Your profile is {dashboardStats.profileCompletion}% complete. Complete your profile to get better job recommendations!
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <WorkIcon />
                </Avatar>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Applied Jobs
                  </Typography>
                  <Typography variant="h5">{dashboardStats.appliedJobs}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                  <BookmarkIcon />
                </Avatar>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Saved Jobs
                  </Typography>
                  <Typography variant="h5">{dashboardStats.savedJobs}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                  <TrendingUpIcon />
                </Avatar>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Profile Views
                  </Typography>
                  <Typography variant="h5">{dashboardStats.profileViews}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Profile Completion
                  </Typography>
                  <Typography variant="h5">{dashboardStats.profileCompletion}%</Typography>
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={dashboardStats.profileCompletion}
                sx={{ height: 8, borderRadius: 5 }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Job Search */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quick Job Search
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Job Title or Keywords"
                value={quickSearchParams.keyword}
                onChange={(e) => setQuickSearchParams({...quickSearchParams, keyword: e.target.value})}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Location"
                value={quickSearchParams.location}
                onChange={(e) => setQuickSearchParams({...quickSearchParams, location: e.target.value})}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Job Type</InputLabel>
                <Select
                  value={quickSearchParams.type}
                  label="Job Type"
                  onChange={(e) => setQuickSearchParams({...quickSearchParams, type: e.target.value})}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="full-time">Full Time</MenuItem>
                  <MenuItem value="part-time">Part Time</MenuItem>
                  <MenuItem value="contract">Contract</MenuItem>
                  <MenuItem value="remote">Remote</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleQuickSearch}
                sx={{ height: '56px' }}
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Main Content Tabs */}
      <Paper sx={{ mb: 4 }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="Overview" />
          <Tab label="Recent Applications" />
          <Tab label="Saved Jobs" />
          <Tab label="Job Recommendations" />
          <Tab label="Notifications" />
        </Tabs>
        
        <Box sx={{ p: 3 }}>
          {/* Overview Tab */}
          {activeTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Recent Activity
                    </Typography>
                    <List>
                      {recentApplications.slice(0, 3).map((application) => (
                        <ListItem key={application.id}>
                          <ListItemIcon>
                            {getStatusIcon(application.status)}
                          </ListItemIcon>
                          <ListItemText
                            primary={`Applied to ${application.jobTitle}`}
                            secondary={`${application.companyName} • ${application.appliedAt}`}
                          />
                          <Chip
                            label={application.status}
                            color={getStatusColor(application.status)}
                            size="small"
                          />
                        </ListItem>
                      ))}
                    </List>
                    <Button
                      variant="outlined"
                      onClick={() => setActiveTab(1)}
                      sx={{ mt: 2 }}
                    >
                      View All Applications
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Profile Actions
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <EditIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Update Profile"
                          secondary="Keep your information current"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <VisibilityIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="View Public Profile"
                          secondary="See how employers see you"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <PersonIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Upload Documents"
                          secondary="Add resume, portfolio, and profile links"
                        />
                      </ListItem>
                    </List>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => navigate('/jobseeker/profile')}
                      sx={{ mt: 2 }}
                    >
                      Manage Profile
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Recent Applications Tab */}
          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Your Job Applications
              </Typography>
              {recentApplications.length > 0 ? (
                <List>
                  {recentApplications.map((application) => (
                    <Card key={application.id} sx={{ mb: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography variant="h6" gutterBottom>
                              {application.jobTitle}
                            </Typography>
                            <Typography color="textSecondary" gutterBottom>
                              {application.companyName} • {typeof application.location === 'object' 
                                ? `${application.location.city || ''}, ${application.location.country || ''}`.replace(/^,\s*|,\s*$/g, '') 
                                : application.location || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Applied on {application.appliedAt}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Chip
                              icon={getStatusIcon(application.status)}
                              label={application.status}
                              color={getStatusColor(application.status)}
                              sx={{ mb: 1 }}
                            />
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                const targetId = application.jobId;
                                if (targetId) {
                                  navigate(`/jobs/${targetId}`);
                                } else {
                                  navigate('/jobseeker/applications');
                                }
                              }}
                            >
                              View Details
                            </Button>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </List>
              ) : (
                <Typography color="textSecondary">
                  You haven't applied to any jobs yet. Start exploring opportunities!
                </Typography>
              )}
            </Box>
          )}

          {/* Saved Jobs Tab */}
          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Your Saved Jobs
              </Typography>
              {savedJobs.length > 0 ? (
                <Grid container spacing={2}>
                  {savedJobs.map((job) => (
                    <Grid item xs={12} key={job._id}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                              <Typography variant="h6" gutterBottom>
                                {job.title}
                              </Typography>
                              <Typography color="textSecondary" gutterBottom>
                                {job.companyName} • {typeof job.location === 'object' 
                                  ? `${job.location.city || ''}, ${job.location.country || ''}`.replace(/^,\s*|,\s*$/g, '') 
                                  : job.location || 'N/A'}
                              </Typography>
                              <Typography variant="body2" paragraph>
                                {job.description?.substring(0, 150)}...
                              </Typography>
                              <Box sx={{ mb: 2 }}>
                                <Chip label={job.type} size="small" sx={{ mr: 1 }} />
                                <Chip label={
                                  job.salary && typeof job.salary === 'object' 
                ? `${formatCurrency(job.salary.min)} - ${formatCurrency(job.salary.max)}`
                                    : job.salary || 'Salary not specified'
                                } size="small" />
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                              <IconButton
                                onClick={() => toggleSaveJob(job._id)}
                                color="primary"
                              >
                                <BookmarkIcon />
                              </IconButton>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => navigate(`/jobs/${job._id}`)}
                                sx={{ mt: 1 }}
                              >
                                View Job
                              </Button>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography color="textSecondary">
                  No saved jobs yet. Save jobs you're interested in to view them here.
                </Typography>
              )}
            </Box>
          )}

          {/* Job Recommendations Tab */}
          {activeTab === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Recommended for You
              </Typography>
              {jobRecommendations.length > 0 ? (
                <Grid container spacing={2}>
                  {jobRecommendations.map((job) => (
                    <Grid item xs={12} key={job._id}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                              <Typography variant="h6" gutterBottom>
                                {job.title}
                              </Typography>
                              <Typography color="textSecondary" gutterBottom>
                                {job.companyName} • {typeof job.location === 'object' 
                                  ? `${job.location.city || ''}, ${job.location.country || ''}`.replace(/^,\s*|,\s*$/g, '') 
                                  : job.location || 'N/A'}
                              </Typography>
                              <Typography variant="body2" paragraph>
                                {job.description?.substring(0, 150)}...
                              </Typography>
                              <Box sx={{ mb: 2 }}>
                                <Chip label={job.type} size="small" sx={{ mr: 1 }} />
                                <Chip label={
                                  job.salary && typeof job.salary === 'object' 
                ? `${formatCurrency(job.salary.min)} - ${formatCurrency(job.salary.max)}`
                                    : job.salary || 'Salary not specified'
                                } size="small" sx={{ mr: 1 }} />
                                <Chip label={`${job.matchScore || 85}% match`} color="success" size="small" />
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                              <IconButton
                                onClick={() => toggleSaveJob(job._id)}
                                color={savedJobs.some(saved => saved._id === job._id) ? 'primary' : 'default'}
                              >
                                {savedJobs.some(saved => saved._id === job._id) ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                              </IconButton>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => navigate(`/jobs/${job._id}`)}
                                sx={{ mt: 1 }}
                              >
                                Apply Now
                              </Button>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography color="textSecondary">
                  Complete your profile to get personalized job recommendations.
                </Typography>
              )}
            </Box>
          )}

          {/* Notifications Tab */}
          {activeTab === 4 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Recent Notifications
              </Typography>
              {notifications.length > 0 ? (
                <List>
                  {notifications.map((notification) => (
                    <Card key={notification.id} sx={{ mb: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <Avatar sx={{ bgcolor: notification.read ? 'grey.300' : 'primary.main', mr: 2 }}>
                            <NotificationsIcon />
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              {notification.title}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" paragraph>
                              {notification.message}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {notification.createdAt}
                            </Typography>
                          </Box>
                          {!notification.read && (
                            <Chip label="New" color="primary" size="small" />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </List>
              ) : (
                <Typography color="textSecondary">
                  No notifications yet.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default Dashboard;